import { useMemo, useState } from 'react';
import { Plus, Briefcase, TrendingUp, Trophy, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useStaff } from '@/features/staff/useStaff';
import { LeadCard } from './LeadCard';
import { LeadModal } from './LeadModal';
import { useLeads, useMoveLeadStage } from './useLeads';
import {
  LEAD_STAGE_ORDER,
  LEAD_STAGE_LABELS,
  LEAD_STAGE_ACCENT,
  type Lead,
  type LeadStage,
} from './types';

const fmtMoney = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

const OPEN_STAGES: LeadStage[] = ['new', 'contacted', 'proposal', 'negotiation'];

export function CommercialPage() {
  const { data: leads = [], isLoading } = useLeads();
  const { data: staff = [] } = useStaff();
  const moveStage = useMoveLeadStage();

  const [modalOpen, setModalOpen] = useState(false);
  const [editLead, setEditLead] = useState<Lead | undefined>();
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const [dragOverStage, setDragOverStage] = useState<LeadStage | null>(null);
  const [lostMove, setLostMove] = useState<{ leadId: string } | null>(null);
  const [lostReason, setLostReason] = useState('');

  const staffNameByUserId = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of staff) {
      if (s.user_id) map.set(s.user_id, s.name);
    }
    return map;
  }, [staff]);

  const byStage = useMemo(() => {
    const groups: Record<LeadStage, Lead[]> = {
      new: [],
      contacted: [],
      proposal: [],
      negotiation: [],
      won: [],
      lost: [],
    };
    for (const l of leads) groups[l.stage].push(l);
    return groups;
  }, [leads]);

  const pipelineValue = useMemo(
    () =>
      leads
        .filter((l) => OPEN_STAGES.includes(l.stage))
        .reduce((s, l) => s + (l.estimated_value ?? 0), 0),
    [leads],
  );
  const openCount = leads.filter((l) => OPEN_STAGES.includes(l.stage)).length;
  const wonCount = byStage.won.length;
  const lostCount = byStage.lost.length;

  const requestMove = (leadId: string, stage: LeadStage) => {
    if (stage === 'lost') {
      setLostReason('');
      setLostMove({ leadId });
      return;
    }
    moveStage.mutate({ id: leadId, stage });
  };

  const confirmLost = () => {
    if (!lostMove) return;
    moveStage.mutate({ id: lostMove.leadId, stage: 'lost', lostReason: lostReason.trim() || null });
    setLostMove(null);
  };

  const openCreate = () => {
    setEditLead(undefined);
    setModalOpen(true);
  };
  const openEdit = (lead: Lead) => {
    setEditLead(lead);
    setModalOpen(true);
  };

  const handleDrop = (stage: LeadStage) => {
    if (draggingId) {
      const lead = leads.find((l) => l.id === draggingId);
      if (lead && lead.stage !== stage) requestMove(draggingId, stage);
    }
    setDraggingId(null);
    setDragOverStage(null);
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-brand text-2xl font-bold text-foreground">Comercial</h1>
          <p className="text-sm text-muted-foreground">
            Funil de prospecção de novos clientes. Arraste os cards entre as etapas.
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-accent font-semibold text-accent-foreground hover:bg-accent/90"
        >
          <Plus size={16} className="mr-2" />
          Novo lead
        </Button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div className="rounded-xl border border-border bg-white p-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Briefcase size={14} /> Leads no funil
          </div>
          <div className="mt-1 font-brand text-2xl font-bold text-primary tabular-nums">
            {openCount}
          </div>
        </div>
        <div className="rounded-xl border border-border bg-white p-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <TrendingUp size={14} /> Valor em pipeline
          </div>
          <div className="mt-1 font-brand text-xl font-bold text-primary tabular-nums">
            {fmtMoney(pipelineValue)}
          </div>
        </div>
        <div className="rounded-xl border border-border bg-white p-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Trophy size={14} /> Ganhos
          </div>
          <div className="mt-1 font-brand text-2xl font-bold text-income tabular-nums">
            {wonCount}
          </div>
        </div>
        <div className="rounded-xl border border-border bg-white p-4">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <XCircle size={14} /> Perdidos
          </div>
          <div className="mt-1 font-brand text-2xl font-bold text-expense tabular-nums">
            {lostCount}
          </div>
        </div>
      </div>

      {/* Kanban */}
      {isLoading ? (
        <div className="flex gap-3 overflow-x-auto">
          {LEAD_STAGE_ORDER.map((s) => (
            <div
              key={s}
              className="h-72 w-72 shrink-0 animate-pulse rounded-xl border border-border bg-muted"
            />
          ))}
        </div>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-2">
          {LEAD_STAGE_ORDER.map((stage) => {
            const stageLeads = byStage[stage];
            const stageValue = stageLeads.reduce((s, l) => s + (l.estimated_value ?? 0), 0);
            return (
              <div
                key={stage}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  if (dragOverStage !== stage) setDragOverStage(stage);
                }}
                onDragLeave={(e) => {
                  // só limpa se realmente saiu da coluna (não de um filho)
                  if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                    setDragOverStage((cur) => (cur === stage ? null : cur));
                  }
                }}
                onDrop={() => handleDrop(stage)}
                className={`flex w-72 shrink-0 flex-col rounded-xl border bg-secondary/30 transition-colors ${
                  dragOverStage === stage
                    ? 'border-primary ring-2 ring-primary/20'
                    : 'border-border'
                }`}
              >
                <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className={`h-2.5 w-2.5 rounded-full ${LEAD_STAGE_ACCENT[stage]}`} />
                    <span className="text-sm font-semibold text-foreground">
                      {LEAD_STAGE_LABELS[stage]}
                    </span>
                    <span className="rounded-full bg-white px-1.5 py-0.5 text-xs text-muted-foreground">
                      {stageLeads.length}
                    </span>
                  </div>
                  {stageValue > 0 && (
                    <span className="text-xs font-medium text-muted-foreground tabular-nums">
                      {fmtMoney(stageValue)}
                    </span>
                  )}
                </div>
                <div className="flex-1 space-y-2 overflow-y-auto p-2" style={{ minHeight: 120 }}>
                  {stageLeads.length === 0 ? (
                    <p className="px-2 py-6 text-center text-xs text-muted-foreground">
                      Nenhum lead aqui.
                    </p>
                  ) : (
                    stageLeads.map((lead) => (
                      <LeadCard
                        key={lead.id}
                        lead={lead}
                        assigneeName={
                          lead.assigned_to_user_id
                            ? (staffNameByUserId.get(lead.assigned_to_user_id) ?? null)
                            : null
                        }
                        onEdit={openEdit}
                        onRequestMove={requestMove}
                        onDragStart={setDraggingId}
                        onDragEnd={() => {
                          setDraggingId(null);
                          setDragOverStage(null);
                        }}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <LeadModal open={modalOpen} onOpenChange={setModalOpen} {...(editLead && { edit: editLead })} />

      {/* Motivo da perda */}
      <Dialog open={!!lostMove} onOpenChange={(v) => !v && setLostMove(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-brand">Marcar lead como perdido</DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="lost-reason">Motivo da perda (opcional)</Label>
            <Textarea
              id="lost-reason"
              value={lostReason}
              onChange={(e) => setLostReason(e.target.value)}
              placeholder="Ex: Preço acima do orçamento, escolheu concorrente…"
              rows={3}
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLostMove(null)}>
              Cancelar
            </Button>
            <Button
              onClick={confirmLost}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Marcar como perdido
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
